import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, Calendar, Heart, MessageSquare, Share2, Send } from 'lucide-react';

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } } };

function AnimSection({ children, className = '' }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return <motion.section ref={ref} variants={stagger} initial="hidden" animate={inView ? 'visible' : 'hidden'} className={className}>{children}</motion.section>;
}

export default function Blog() {
  const { apiClient } = useAuth();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentName, setCommentName] = useState('');

  const loadComments = useCallback(async (postId) => {
    try { const { data } = await apiClient.get(`/api/blog/${postId}/comments`); setComments(data); } catch {}
  }, [apiClient]);

  const fetchData = useCallback(async () => {
    try {
      const [postsRes, catsRes] = await Promise.all([apiClient.get('/api/blog'), apiClient.get('/api/blog-categories')]);
      setPosts(postsRes.data);
      setCategories(catsRes.data);
      const params = new URLSearchParams(window.location.search);
      const postParam = params.get('post');
      if (postParam) {
        const p = postsRes.data.find(x => x.id === postParam);
        if (p) { setSelectedPost(p); loadComments(p.id); }
      }
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [apiClient, loadComments]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilter = async (cat) => {
    setSelected(cat);
    setLoading(true);
    try { const { data } = await apiClient.get(`/api/blog${cat !== 'all' ? `?category=${cat}` : ''}`); setPosts(data); }
    catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const handleLike = async (postId) => {
    try { await apiClient.post(`/api/blog/${postId}/like`); setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes: (p.likes || 0) + 1 } : p)); if (selectedPost?.id === postId) setSelectedPost(prev => ({ ...prev, likes: (prev.likes || 0) + 1 })); toast.success('Liked!'); }
    catch { toast.error('Failed'); }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    try {
      const { data } = await apiClient.post(`/api/blog/${selectedPost.id}/comment`, { content: commentText, author_name: commentName || undefined });
      setComments(prev => [...prev, data]);
      setCommentText('');
      toast.success('Comment added!');
    } catch { toast.error('Failed'); }
  };

  const sharePost = (postId) => {
    const url = `${window.location.origin}/blog?post=${postId}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Link copied!')).catch(() => toast.error('Failed'));
  };

  const openPost = (post) => { setSelectedPost(post); loadComments(post.id); window.history.pushState({}, '', `/blog?post=${post.id}`); };
  const closePost = () => { setSelectedPost(null); setComments([]); window.history.pushState({}, '', '/blog'); };

  if (selectedPost) {
    return (
      <div className="pt-16 min-h-screen" data-testid="blog-post-detail">
        <div className="relative h-[50vh]">
          <img src={selectedPost.image_url || 'https://images.unsplash.com/photo-1746021513193-a314945d15c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920'} alt={selectedPost.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--text-headline)] via-[var(--text-headline)]/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-6 pb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-[var(--primary-green)] text-[var(--text-headline)] text-xs font-bold mb-3">{selectedPost.category}</span>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white">{selectedPost.title}</h1>
            <p className="text-white/60 text-sm mt-2">{new Date(selectedPost.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="flex items-center justify-between mb-8">
            <button onClick={closePost} className="text-[var(--primary-blue)] font-semibold text-sm inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="back-to-blog">&larr; Back to Blog</button>
            <div className="flex gap-3">
              <button onClick={() => handleLike(selectedPost.id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-[var(--border-color)] text-sm hover:bg-red-50 hover:text-red-500 transition-colors" data-testid="like-btn"><Heart size={14} /> {selectedPost.likes || 0}</button>
              <button onClick={() => sharePost(selectedPost.id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-[var(--border-color)] text-sm hover:bg-blue-50 hover:text-[var(--primary-blue)] transition-colors" data-testid="share-btn"><Share2 size={14} /> Share</button>
            </div>
          </div>
          <div className="prose prose-lg max-w-none" data-testid="blog-content">
            {selectedPost.content.split('\n').map((line, i) => {
              const key = `line-${i}`;
              if (line.startsWith('## ')) return <h2 key={key} className="text-xl font-heading font-bold mt-8 mb-3 text-[var(--text-headline)]">{line.replace('## ', '')}</h2>;
              if (line.startsWith('- ')) return <li key={key} className="text-[var(--text-body)] ml-4 mb-1">{line.replace('- ', '')}</li>;
              if (line.match(/^\d+\./)) return <li key={key} className="text-[var(--text-body)] ml-4 mb-1 list-decimal">{line.replace(/^\d+\.\s*/, '')}</li>;
              if (line.trim() === '') return <br key={key} />;
              return <p key={key} className="text-[var(--text-body)] mb-4 leading-relaxed">{line}</p>;
            })}
          </div>

          {/* Comments Section */}
          <div className="mt-12 border-t border-[var(--border-color)] pt-8" data-testid="comments-section">
            <h3 className="text-xl font-heading font-bold mb-6 flex items-center gap-2"><MessageSquare size={20} /> Comments ({comments.length})</h3>
            <form onSubmit={handleComment} className="mb-8 card-elevated p-4 space-y-3">
              <input placeholder="Your name (optional)" value={commentName} onChange={e => setCommentName(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-[var(--border-color)] bg-[var(--bg-alt)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-blue)]" data-testid="comment-name-input" />
              <div className="flex gap-2">
                <input placeholder="Write a comment..." value={commentText} onChange={e => setCommentText(e.target.value)} required className="flex-1 px-4 py-2.5 rounded-xl border border-[var(--border-color)] bg-[var(--bg-alt)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-blue)]" data-testid="comment-text-input" />
                <button type="submit" className="btn-primary !px-4 !py-2.5" data-testid="comment-submit-btn"><Send size={16} /></button>
              </div>
            </form>
            {comments.length === 0 ? <p className="text-[var(--text-body)] text-sm">No comments yet. Be the first!</p> : (
              <div className="space-y-4">
                {comments.map(c => (
                  <div key={c.id} className="p-4 rounded-xl bg-[var(--bg-alt)] border border-[var(--border-color)]">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded-full bg-[var(--primary-blue)] text-white text-xs font-bold flex items-center justify-center">{(c.author_name || 'A')[0]}</div>
                      <span className="font-heading font-bold text-sm">{c.author_name}</span>
                      <span className="text-xs text-[var(--text-body)]">{new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-[var(--text-body)]">{c.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 min-h-screen" data-testid="blog-page">
      <section className="relative h-[40vh] flex items-end">
        <img src="https://images.unsplash.com/photo-1746021513193-a314945d15c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="Blog" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--text-headline)] via-[var(--text-headline)]/30 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-10 w-full">
          <p className="uppercase tracking-[0.3em] text-xs font-bold text-[var(--primary-green)] mb-3">Resources & Insights</p>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-white">Blog</h1>
        </div>
      </section>

      <AnimSection className="section-padding">
        <div className="max-w-7xl mx-auto">
          {/* Category Filter */}
          <motion.div variants={fadeUp} className="flex flex-wrap gap-3 mb-10" data-testid="blog-category-filter">
            <button onClick={() => handleFilter('all')} className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all ${selected === 'all' ? 'bg-[var(--primary-blue)] text-white shadow-lg shadow-blue-500/20' : 'bg-[var(--bg-alt)] text-[var(--text-body)] border border-[var(--border-color)] hover:border-[var(--primary-blue)]'}`} data-testid="blog-filter-all">All</button>
            {categories.map((c) => (
              <button key={c} onClick={() => handleFilter(c)} className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all ${selected === c ? 'bg-[var(--primary-blue)] text-white shadow-lg shadow-blue-500/20' : 'bg-[var(--bg-alt)] text-[var(--text-body)] border border-[var(--border-color)] hover:border-[var(--primary-blue)]'}`} data-testid={`blog-filter-${c.toLowerCase()}`}>{c}</button>
            ))}
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary-blue)]" /></div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16"><p className="text-[var(--text-body)]">No posts found.</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, i) => (
                <motion.div key={post.id} variants={fadeUp} className="card-elevated overflow-hidden group cursor-pointer" onClick={() => openPost(post)} data-testid={`blog-post-${post.id}`}>
                  <div className="h-48 overflow-hidden relative">
                    <img src={post.image_url || 'https://images.unsplash.com/photo-1746021513193-a314945d15c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=400'} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[var(--primary-green)] text-[var(--text-headline)] text-xs font-bold">{post.category}</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-xs text-[var(--text-body)] mb-3">
                      <Calendar size={12} /> {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                    <h3 className="text-lg font-heading font-bold mb-2 group-hover:text-[var(--primary-blue)] transition-colors">{post.title}</h3>
                    <p className="text-sm text-[var(--text-body)] mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-3 text-xs text-[var(--text-body)]">
                        <button onClick={(e) => { e.stopPropagation(); handleLike(post.id); }} className="flex items-center gap-1 hover:text-red-500 transition-colors"><Heart size={12} /> {post.likes || 0}</button>
                        <span className="flex items-center gap-1"><MessageSquare size={12} /> {post.comments?.length || 0}</span>
                        <button onClick={(e) => { e.stopPropagation(); sharePost(post.id); }} className="flex items-center gap-1 hover:text-[var(--primary-blue)] transition-colors"><Share2 size={12} /></button>
                      </div>
                      <span className="text-sm text-[var(--primary-blue)] font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">Read <ArrowRight size={14} /></span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </AnimSection>
    </div>
  );
}
